import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { getTiersAbove, getTierThreshold } from 'loyalty/selectors/getLoyaltyTierThresholds'
import moment from 'moment'

export interface TierProgressionEligibilityInput {
  currentTier: App.LoyaltyTier
  currentStatusCredits: number
  currentNights: number
  statusCredits: number
  nights: number
  pointsEarnDate?: string
  membershipYearEndDate?: string
  pendingPoints?: Array<App.LoyaltyAccountPendingPoints>
  tierThresholds: Record<App.LoyaltyTier, App.LoyaltyTierThreshold>
}

export type TierProgressionEligibilityOutput =
  | {
    isEligible: true
    /** The lowest tier that can be unlocked with this package/rate */
    lowestUnlockableTier: App.LoyaltyTier
    lowestUnlockableTierName: string
    /** The highest tier that can be reached with this package/rate */
    highestUnlockableTier: App.LoyaltyTier
    highestUnlockableTierName: string
    /** True if user can jump multiple tiers (lowest !== highest) */
    hasMultipleTierJumps: boolean
  }
  | {
    isEligible: false
    lowestUnlockableTier: undefined
    lowestUnlockableTierName: undefined
    highestUnlockableTier: undefined
    highestUnlockableTierName: undefined
    hasMultipleTierJumps: false
  }

const notEligible = {
  isEligible: false,
  lowestUnlockableTier: undefined,
  lowestUnlockableTierName: undefined,
  highestUnlockableTier: undefined,
  highestUnlockableTierName: undefined,
  hasMultipleTierJumps: false,
} as const
/**
 * This logic should be moved to backend to be reused for front end and back end
 *
 */
export function calculateTierProgressionEligibility(
  input: TierProgressionEligibilityInput,
): TierProgressionEligibilityOutput {
  const {
    currentTier,
    currentStatusCredits,
    currentNights,
    statusCredits,
    nights,
    pointsEarnDate,
    membershipYearEndDate,
    pendingPoints = [],
    tierThresholds,
  } = input

  if (pointsEarnDate && membershipYearEndDate) {
    const earnDate = moment(pointsEarnDate)
    const membershipYearEnd = moment(membershipYearEndDate)

    if (earnDate.isAfter(membershipYearEnd)) {
      return notEligible
    }
  }

  let pendingStatusCredits = 0
  let pendingNights = 0

  if (membershipYearEndDate && pendingPoints.length > 0) {
    const membershipYearEnd = moment(membershipYearEndDate)
    for (const pending of pendingPoints) {
      if (pending.pointsEarnDate) {
        const earnDate = moment(pending.pointsEarnDate)
        if (earnDate.isSameOrBefore(membershipYearEnd)) {
          pendingStatusCredits += pending.statusCredits
          pendingNights += pending.numNights ?? 0
        }
      }
    }
  }

  const currentPlusPendingCredits = currentStatusCredits + pendingStatusCredits
  const currentPlusPendingNights = currentNights + pendingNights
  const totalStatusCreditsAfterBooking = currentPlusPendingCredits + statusCredits
  const totalNightsAfterBooking = currentPlusPendingNights + nights

  const tiersAbove = getTiersAbove(currentTier)

  if (tiersAbove.length === 0) {
    return notEligible
  }

  let highestUnlockableTier: App.LoyaltyTier | undefined
  let lowestUnlockableTier: App.LoyaltyTier | undefined

  // Iterate from lowest to highest tier, only counting tiers that
  // genuinely require this purchase (skip tiers the user already qualifies for)
  for (let i = 0; i < tiersAbove.length; i++) {
    const targetTier = tiersAbove[i]
    const requirements = getTierThreshold(tierThresholds, targetTier)

    const meetsAfterBooking =
      totalStatusCreditsAfterBooking >= requirements.statusCredits &&
      totalNightsAfterBooking >= requirements.nights

    const alreadyMeetsWithoutBooking =
      currentPlusPendingCredits >= requirements.statusCredits &&
      currentPlusPendingNights >= requirements.nights

    if (meetsAfterBooking && !alreadyMeetsWithoutBooking) {
      if (!lowestUnlockableTier) {
        lowestUnlockableTier = targetTier
      }
      highestUnlockableTier = targetTier
    }
  }

  if (!lowestUnlockableTier || !highestUnlockableTier) {
    return notEligible
  }

  return {
    isEligible: true,
    lowestUnlockableTier,
    lowestUnlockableTierName: getLoyaltyTierDisplayName(lowestUnlockableTier),
    highestUnlockableTier,
    highestUnlockableTierName: getLoyaltyTierDisplayName(highestUnlockableTier),
    hasMultipleTierJumps: lowestUnlockableTier !== highestUnlockableTier,
  }
}
